import { useContext, useState, SyntheticEvent } from "react";
import { useQuery } from "@apollo/client";
import dayjs from "dayjs";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import { UserContext } from "@/App";
import { formatAmount } from "@/utils/format";
import { ActionsBar } from "@/components/layout";
import { TabsStyled, TabStyled } from "@/shared";
import { Box } from "@/components/ui/Box";
import { Skeleton } from "@/components/ui/Skeleton";
import { ChartBudgetExpense } from "@/components/chart-budget-expense/chart-budget-expense";
import { SharedSpendSplit } from "@/components/shared-spend-split/shared-spend-split";
import { ChartPie } from "@/components/chart-pie/chart-pie";
import { useScope, scopeVariables } from "@/features/groups/scope-context";
import { GET_CHART_EXPENSES_LIST } from "@/pages/expenses-page/expenses-page-queries";
import { tokens } from "@/theme/tokens";
import { GET_INSIGHTS } from "./insights-page-queries";
import {
  InsightsWrapperStyled,
  SectionStyled,
  SectionTitleStyled,
  HeroAmountStyled,
  SubtleTextStyled,
  RowStyled,
  RowMainStyled,
  RowTitleStyled,
  RowRightStyled,
  SummaryHeadStyled,
  SummaryCompareStyled,
  PaceRowStyled,
  BarTrackStyled,
  DeltaStyled,
  StreakBadgeStyled,
  EmptyTextStyled,
} from "./insights-page-style";

// Over a self-set budget is bad news; on track is good news. Both are money
// values, so they take money tokens.
const OVER = tokens.money.negative;
const UNDER = tokens.money.positive;

interface Props {
  pageDate: Date;
  onClickNext: () => void;
  onClickPrevious: () => void;
}

export const InsightsPageContainer = ({
  pageDate,
  onClickNext,
  onClickPrevious,
}: Props) => {
  const userCurrency = useContext(UserContext);
  const scope = useScope();
  const formattedDate = dayjs(pageDate).format("MM-DD-YYYY");

  const { data, loading } = useQuery(GET_INSIGHTS, {
    variables: { date: formattedDate, ...scopeVariables(scope) },
    fetchPolicy: "cache-and-network",
  });

  const { data: chartData, loading: loadingChart } = useQuery(
    GET_CHART_EXPENSES_LIST,
    {
      variables: { date: formattedDate, ...scopeVariables(scope) },
      fetchPolicy: "cache-and-network",
    },
  );

  const insights = data?.insights;
  const fmt = (n: number) => formatAmount(n, userCurrency);

  const chartExpensesData: number[] =
    chartData?.chartExpenses?.monthlyTotals || [];
  const chartCategoriesData =
    chartData?.chartExpenses?.categoryExpenseTotals || [];
  // Per-month, from the schedule. `insights.totalBudget` is the viewed month
  // only and would flatten a year that contains a budget change.
  const monthlyBudgets: number[] =
    chartData?.chartExpenses?.monthlyBudgets || [];
  const [tabIndex, setTabIndex] = useState(0);
  const handleTabChange = (_: SyntheticEvent, next: number) =>
    setTabIndex(next);

  const renderDelta = (delta: number, percent?: number | null) => {
    const up = delta > 0;
    return (
      <DeltaStyled up={up}>
        {up ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
        {fmt(Math.abs(delta))}
        {percent != null && (
          <span>&nbsp;({Math.abs(Math.round(percent))}%)</span>
        )}
      </DeltaStyled>
    );
  };

  return (
    <>
      <ActionsBar
        showScope
        pageDate={pageDate}
        onClickNext={onClickNext}
        onClickPrevious={onClickPrevious}
      />

      <InsightsWrapperStyled>
        {/* Overview charts */}
        {/* The chart lives in its own Box rather than a SectionStyled, so it
            needs the same card treatment explicitly or it stays unfilled. */}
        <Box
          sx={{
            paddingTop: "12px",
            background: tokens.surface,
            border: `1px solid ${tokens.hairline}`,
            borderRadius: `${tokens.radius.md}px`,
            boxShadow: "0 1px 2px rgba(20, 18, 15, 0.04)",
          }}
        >
          <TabsStyled
            value={tabIndex}
            onChange={handleTabChange}
            aria-label="Insights charts"
          >
            <TabStyled label="Yearly expense" />
            <TabStyled label="Expense by category" />
          </TabsStyled>

          <Box sx={{ padding: 1 }}>
            {loadingChart && <Skeleton animation="wave" height={300} />}
            {!loadingChart &&
              (() => {
                const hasValidExpenseData =
                  Array.isArray(chartExpensesData) &&
                  chartExpensesData.length === 12;
                const hasValidCategoriesData =
                  Array.isArray(chartCategoriesData);

                if (!hasValidExpenseData || !hasValidCategoriesData) {
                  return (
                    <Box sx={{ p: 4, textAlign: "center" }}>
                      <EmptyTextStyled>
                        No chart data to display.
                      </EmptyTextStyled>
                    </Box>
                  );
                }

                if (tabIndex === 0) {
                  return (
                    <ChartBudgetExpense
                      monthlyBudgets={monthlyBudgets}
                      sharedByUser={
                        chartData?.chartExpenses?.sharedMonthlyByUser || []
                      }
                      chartExpensesData={chartExpensesData}
                      pageDate={pageDate}
                    />
                  );
                }

                if (tabIndex === 1 && chartCategoriesData.length > 0) {
                  return <ChartPie data={chartCategoriesData} />;
                }

                return null;
              })()}
          </Box>
        </Box>

        {!insights && loading && (
          <EmptyTextStyled>Loading insights...</EmptyTextStyled>
        )}

        {insights && (
          <>
            {/* One card, not two: safe-to-spend and the month-over-month
                comparison answer the same question, and the second used to
                restate a figure the first already showed. */}
            <SectionStyled data-tour="insights-this-month">
              <SectionTitleStyled>This month</SectionTitleStyled>

              <SummaryHeadStyled>
                <div>
                  <HeroAmountStyled negative={insights.totalSafeToSpend < 0}>
                    {fmt(insights.totalSafeToSpend)}
                  </HeroAmountStyled>
                  <SubtleTextStyled>safe to spend</SubtleTextStyled>
                </div>

                <SummaryCompareStyled>
                  {renderDelta(
                    insights.monthOverMonthDelta,
                    insights.monthOverMonthPercent,
                  )}
                  <SubtleTextStyled>
                    vs {fmt(insights.previousMonthTotal)} last month
                  </SubtleTextStyled>
                </SummaryCompareStyled>
              </SummaryHeadStyled>

              <SubtleTextStyled style={{ marginTop: 12 }}>
                {fmt(insights.totalSpent)} spent of {fmt(insights.totalBudget)}{" "}
                budget &middot; day {insights.daysElapsed} of{" "}
                {insights.daysInMonth}
              </SubtleTextStyled>
              <SubtleTextStyled style={{ marginTop: 4 }}>
                Projected month end:{" "}
                <span
                  style={{
                    color:
                      insights.totalProjected > insights.totalBudget
                        ? OVER
                        : UNDER,
                    fontWeight: 600,
                  }}
                >
                  {fmt(insights.totalProjected)}
                </span>
                {insights.totalProjected > insights.totalBudget &&
                  insights.totalBudget > 0 &&
                  ` (over by ${fmt(insights.totalProjected - insights.totalBudget)})`}
              </SubtleTextStyled>
            </SectionStyled>

            {/* Biggest movers */}
            <SectionStyled>
              <SectionTitleStyled>
                Biggest movers vs last month
              </SectionTitleStyled>
              {insights.biggestMovers.length === 0 && (
                <EmptyTextStyled>No change from last month.</EmptyTextStyled>
              )}
              {insights.biggestMovers.map((m: any) => (
                <RowStyled key={m.categoryId}>
                  <RowMainStyled>
                    <RowTitleStyled>{m.categoryName}</RowTitleStyled>
                    <SubtleTextStyled>
                      {fmt(m.previousTotal)} &rarr; {fmt(m.currentTotal)}
                    </SubtleTextStyled>
                  </RowMainStyled>
                  {renderDelta(m.delta, m.percentChange)}
                </RowStyled>
              ))}
            </SectionStyled>

            {/* Top expenses */}
            <SectionStyled>
              <SectionTitleStyled>Top expenses</SectionTitleStyled>
              {insights.topExpenses.length === 0 && (
                <EmptyTextStyled>No expenses this month.</EmptyTextStyled>
              )}
              {insights.topExpenses.map((e: any) => (
                <RowStyled key={e.id}>
                  <RowMainStyled>
                    <RowTitleStyled>
                      {e.description || e.subcategoryName}
                    </RowTitleStyled>
                    <SubtleTextStyled>
                      {e.categoryName} &middot; {e.subcategoryName} &middot;{" "}
                      {dayjs(Number(e.date)).format("D MMM")}
                      {e.paidByName ? ` · ${e.paidByName}` : ""}
                    </SubtleTextStyled>
                  </RowMainStyled>
                  <RowRightStyled>
                    <span style={{ fontWeight: 600 }}>{fmt(e.amount)}</span>
                  </RowRightStyled>
                </RowStyled>
              ))}
            </SectionStyled>

            {/* On-budget streaks */}
            <SectionStyled>
              <SectionTitleStyled>On-budget streaks</SectionTitleStyled>
              {insights.streaks.length === 0 && (
                <EmptyTextStyled>
                  No on-budget streaks yet. Stay under budget to start one.
                </EmptyTextStyled>
              )}
              {insights.streaks.map((s: any) => (
                <RowStyled key={s.subcategoryId}>
                  <RowMainStyled>
                    <RowTitleStyled>{s.subcategoryName}</RowTitleStyled>
                    <SubtleTextStyled>{s.categoryName}</SubtleTextStyled>
                  </RowMainStyled>
                  <StreakBadgeStyled>
                    <LocalFireDepartmentIcon fontSize="small" />
                    {s.monthsUnderBudget} mo
                  </StreakBadgeStyled>
                </RowStyled>
              ))}
            </SectionStyled>

            {/* Who paid, in shared categories. Hidden entirely when nothing is
                shared, since there is then nobody to compare against. */}
            {insights.sharedTotalsByUser.length > 0 && (
              <SectionStyled data-tour="insights-shared">
                <SectionTitleStyled>Shared spend by person</SectionTitleStyled>
                {insights.sharedSplits.length === 0 ? (
                  <EmptyTextStyled>
                    No shared spending this month.
                  </EmptyTextStyled>
                ) : (
                  <SharedSpendSplit
                    totalsByUser={insights.sharedTotalsByUser}
                    splits={insights.sharedSplits}
                  />
                )}
              </SectionStyled>
            )}

            {/* Pace by category */}
            <SectionStyled data-tour="insights-pace">
              <SectionTitleStyled>Pace by category</SectionTitleStyled>
              {insights.pace.filter((p: any) => p.budget > 0).length === 0 && (
                <EmptyTextStyled>No budgets set for this view.</EmptyTextStyled>
              )}
              {[...insights.pace]
                .filter((p: any) => p.budget > 0)
                .sort((a: any, b: any) => b.percentUsed - a.percentUsed)
                .map((p: any) => {
                  const over = p.projected > p.budget;
                  return (
                    <PaceRowStyled key={p.categoryId}>
                      <RowMainStyled>
                        <RowTitleStyled>{p.categoryName}</RowTitleStyled>
                      </RowMainStyled>
                      <BarTrackStyled>
                        <div
                          style={{
                            height: "100%",
                            width: `${Math.min(100, Math.round(p.percentUsed))}%`,
                            background: over ? OVER : UNDER,
                          }}
                        />
                      </BarTrackStyled>
                      <RowRightStyled>
                        <span>
                          {fmt(p.spent)} / {fmt(p.budget)}
                        </span>
                        <SubtleTextStyled
                          style={{ color: over ? OVER : undefined }}
                        >
                          proj. {fmt(p.projected)}
                        </SubtleTextStyled>
                      </RowRightStyled>
                    </PaceRowStyled>
                  );
                })}
            </SectionStyled>
          </>
        )}
      </InsightsWrapperStyled>
    </>
  );
};
